import React, { useEffect, useMemo, useState } from "react";
import { getRequestDocumentId, useAppStore } from "../store/useAppStore";
import Pagination from "./Pagination";
import RequestMergeModal from "./RequestMergeModal";
import RequestPlaceholderUsageModal from "./RequestPlaceholderUsageModal";

const userFilterKey = (user) => {
  if (!user) return "";
  if (user.id != null && user.id !== "") return `id:${user.id}`;
  if (user.documentId) return `doc:${user.documentId}`;
  if (user.email) return `email:${String(user.email).toLowerCase()}`;
  if (user.username) return `name:${user.username}`;
  return "";
};

const userFilterLabel = (user) => {
  if (!user) return "—";
  return user.username || user.email || (user.id != null ? `ID ${user.id}` : "Пользователь");
};

const SORT_OPTIONS = [
  { value: "default", label: "По умолчанию" },
  { value: "rating:desc", label: "Рейтинг: выше" },
  { value: "rating:asc", label: "Рейтинг: ниже" },
];

const formatRating = (rating) => (rating != null ? String(rating) : "—");

const RequestsPage = () => {
  const PAGE_SIZE_OPTIONS = [10, 15, 20, 25, 50];
  const signRequests = useAppStore((state) => state.signRequests);
  const signRequestsLoading = useAppStore((state) => state.signRequestsLoading);
  const signRequestsError = useAppStore((state) => state.signRequestsError);
  const signRequestsMutationLoading = useAppStore((state) => state.signRequestsMutationLoading);
  const signRequestsMutationError = useAppStore((state) => state.signRequestsMutationError);
  const fetchSignRequests = useAppStore((state) => state.fetchSignRequests);
  const updateSignRequestStatus = useAppStore((state) => state.updateSignRequestStatus);
  const deleteSignRequest = useAppStore((state) => state.deleteSignRequest);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchField, setSearchField] = useState("lemma");
  const [statusFilter, setStatusFilter] = useState("all");
  const [userFilter, setUserFilter] = useState("");
  const [notesPresenceFilter, setNotesPresenceFilter] = useState("all");
  const [sortBy, setSortBy] = useState("default");
  const [viewMode, setViewMode] = useState("cards");
  const [mergeRequest, setMergeRequest] = useState(null);
  const [usageRequest, setUsageRequest] = useState(null);
  const [mergeSuccessMessage, setMergeSuccessMessage] = useState("");
  const signRequestUsageLoading = useAppStore((state) => state.signRequestUsageLoading);

  useEffect(() => {
    fetchSignRequests();
  }, [fetchSignRequests]);

  useEffect(() => {
    const nextQuery = searchInput.trim();
    if (nextQuery === searchTerm) return undefined;

    const timer = setTimeout(() => {
      setCurrentPage(1);
      setSearchTerm(nextQuery);
    }, 350);

    return () => clearTimeout(timer);
  }, [searchInput, searchTerm]);

  const userFilterOptions = useMemo(() => {
    const byKey = new Map();
    for (const request of signRequests) {
      const u = request?.user;
      const key = userFilterKey(u);
      if (!key || byKey.has(key)) continue;
      byKey.set(key, { key, label: userFilterLabel(u) });
    }
    return Array.from(byKey.values()).sort((a, b) =>
      a.label.localeCompare(b.label, "ru", { sensitivity: "base" }),
    );
  }, [signRequests]);

  const filteredRequests = useMemo(() => {
    const query = searchTerm.toLowerCase();
    const hasTextSearch = Boolean(searchTerm);

    return signRequests.filter((request) => {
      if (statusFilter === "new" && request.added) return false;
      if (statusFilter === "added" && !request.added) return false;

      if (userFilter && userFilterKey(request.user) !== userFilter) return false;

      const notesTrimmed = (request?.notes || "").trim();
      if (notesPresenceFilter === "with" && !notesTrimmed) return false;
      if (notesPresenceFilter === "without" && notesTrimmed) return false;

      if (!hasTextSearch) return true;

      let value = "";
      if (searchField === "notes") {
        value = request?.notes || "";
      } else if (searchField === "user") {
        value = `${request?.user?.username || ""} ${request?.user?.email || ""}`;
      } else {
        value = request?.lemma || "";
      }

      return String(value).toLowerCase().includes(query);
    });
  }, [signRequests, searchTerm, searchField, statusFilter, userFilter, notesPresenceFilter]);

  const sortedRequests = useMemo(() => {
    const list = [...filteredRequests];

    if (sortBy === "rating:desc") {
      return list.sort((a, b) => {
        const aRating = a.rating ?? -Infinity;
        const bRating = b.rating ?? -Infinity;
        return bRating - aRating;
      });
    }

    if (sortBy === "rating:asc") {
      return list.sort((a, b) => {
        const aRating = a.rating ?? Infinity;
        const bRating = b.rating ?? Infinity;
        return aRating - bRating;
      });
    }

    return list;
  }, [filteredRequests, sortBy]);

  const hasActiveFilters =
    statusFilter !== "all" || Boolean(userFilter) || notesPresenceFilter !== "all";

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(sortedRequests.length / itemsPerPage));
  }, [sortedRequests.length, itemsPerPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedRequests = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedRequests.slice(start, start + itemsPerPage);
  }, [sortedRequests, currentPage, itemsPerPage]);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handlePageSizeChange = (event) => {
    const value = Number(event.target.value);
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, userFilter, notesPresenceFilter, sortBy]);

  const handleToggleStatus = async (request) => {
    await updateSignRequestStatus(request, !request?.added);
  };

  const handleDeleteRequest = async (request) => {
    const title = request?.lemma || getRequestDocumentId(request) || "—";
    const confirmed = window.confirm(`Удалить заявку ${title}?`);
    if (!confirmed) return;

    const ok = await deleteSignRequest(request);
    if (!ok) return;

    const nextTotalPages = Math.max(1, Math.ceil((sortedRequests.length - 1) / itemsPerPage));
    if (currentPage > nextTotalPages) {
      setCurrentPage(nextTotalPages);
    }
  };

  const renderStatusBadge = (request) => (
    <span className={`status-badge ${request.added ? "added" : "new"}`}>
      {request.added ? "Добавлено" : "Новая"}
    </span>
  );

  const renderActionButtons = (request) => (
    <>
      <button
        className="e-btn requests-usage-btn"
        type="button"
        onClick={() => setUsageRequest(request)}
        disabled={signRequestsMutationLoading || signRequestUsageLoading}
        title="Где используется заглушка"
      >
        <i className="fa-solid fa-link" aria-hidden="true"></i>
        Где используется
      </button>
      <button
        className="e-btn requests-merge-btn"
        type="button"
        onClick={() => {
          setMergeSuccessMessage("");
          setMergeRequest(request);
        }}
        disabled={signRequestsMutationLoading}
      >
        <i className="fa-solid fa-code-merge" aria-hidden="true"></i>
        Merge
      </button>
      <button
        className="e-btn requests-status-btn"
        type="button"
        onClick={() => handleToggleStatus(request)}
        disabled={signRequestsMutationLoading}
      >
        {request.added ? "Вернуть в новые" : "Отметить добавленным"}
      </button>
      <button
        className="danger-btn"
        type="button"
        onClick={() => handleDeleteRequest(request)}
        disabled={signRequestsMutationLoading}
      >
        <i className="fa-solid fa-trash" aria-hidden="true"></i>
        Удалить
      </button>
    </>
  );

  const renderRequestActions = (request) => (
    <>
      {renderStatusBadge(request)}
      {renderActionButtons(request)}
    </>
  );

  return (
    <main className="main-page">
      <div className="main-card">
        <div className="requests-head">
          <h1>
            <i className="fa-solid fa-clipboard-list" aria-hidden="true"></i> Заявки
          </h1>
          <div className="requests-head-actions">
            <div className="requests-view-toggle" role="group" aria-label="Вид отображения">
              <button
                className={`requests-view-btn ${viewMode === "cards" ? "active" : ""}`}
                type="button"
                onClick={() => setViewMode("cards")}
                aria-pressed={viewMode === "cards"}
                title="Карточки"
              >
                <i className="fa-solid fa-grip" aria-hidden="true"></i>
                Карточки
              </button>
              <button
                className={`requests-view-btn ${viewMode === "table" ? "active" : ""}`}
                type="button"
                onClick={() => setViewMode("table")}
                aria-pressed={viewMode === "table"}
                title="Таблица"
              >
                <i className="fa-solid fa-table" aria-hidden="true"></i>
                Таблица
              </button>
            </div>
            <button className="e-btn" type="button" onClick={fetchSignRequests}>
              <i className="fa-solid fa-rotate-right" aria-hidden="true"></i>
            </button>
          </div>
        </div>

        <section className="requests-filters" aria-label="Фильтры заявок">
          <div className="requests-filters-row">
            <div className="requests-filter-field">
              <label htmlFor="requests-filter-status">Статус</label>
              <select
                id="requests-filter-status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">Все</option>
                <option value="new">Новая</option>
                <option value="added">Добавлено</option>
              </select>
            </div>
            <div className="requests-filter-field">
              <label htmlFor="requests-filter-user">Пользователь</label>
              <select
                id="requests-filter-user"
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
              >
                <option value="">Все</option>
                {userFilterOptions.map((opt) => (
                  <option key={opt.key} value={opt.key}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="requests-filter-field">
              <label htmlFor="requests-filter-notes">Заметки</label>
              <select
                id="requests-filter-notes"
                value={notesPresenceFilter}
                onChange={(e) => setNotesPresenceFilter(e.target.value)}
              >
                <option value="all">Все</option>
                <option value="with">С заметками</option>
                <option value="without">Без заметок</option>
              </select>
            </div>
            <div className="requests-filter-field">
              <label htmlFor="requests-filter-sort">Сортировка</label>
              <select
                id="requests-filter-sort"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="requests-filter-field requests-filter-field--page-size">
              <label htmlFor="requests-page-size">На странице</label>
              <select id="requests-page-size" value={itemsPerPage} onChange={handlePageSizeChange}>
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <label className="gesture-search requests-filters-search" htmlFor="requests-search">
            <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
            <select
              value={searchField}
              onChange={(event) => {
                const nextField = event.target.value;
                setSearchField(nextField === "notes" || nextField === "user" ? nextField : "lemma");
                setCurrentPage(1);
              }}
              aria-label="Поле поиска"
            >
              <option value="lemma">lemma</option>
              <option value="notes">notes</option>
              <option value="user">user</option>
            </select>
            <input
              id="requests-search"
              type="text"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder={`Поиск по ${searchField}`}
            />
            {searchInput ? (
              <button className="gesture-search-clear" type="button" onClick={() => setSearchInput("")}>
                <i className="fa-solid fa-xmark" aria-hidden="true"></i>
              </button>
            ) : null}
          </label>
        </section>

        {signRequestsLoading ? <p><i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i> Загрузка заявок...</p> : null}
        {signRequestsError ? <p className="requests-error"><i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i> {signRequestsError}</p> : null}
        {signRequestsMutationError ? <p className="requests-error"><i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i> {signRequestsMutationError}</p> : null}
        {mergeSuccessMessage ? (
          <p className="dashboard-empty">
            <i className="fa-solid fa-circle-check" aria-hidden="true"></i> {mergeSuccessMessage}
          </p>
        ) : null}

        {!signRequestsLoading ? (
          <p>
            <i className="fa-solid fa-layer-group" aria-hidden="true"></i>
            Всего: {filteredRequests.length} | Страница: {currentPage} из {totalPages}
            {hasActiveFilters ? " | Фильтры включены" : ""}
            {searchTerm ? ` | Поиск (${searchField}): «${searchTerm}»` : ""}
          </p>
        ) : null}

        {!signRequestsLoading && !filteredRequests.length ? (
          <p>
            <i className="fa-regular fa-folder-open" aria-hidden="true"></i>
            {searchTerm || hasActiveFilters
              ? " По выбранным фильтрам и запросу ничего не найдено."
              : " Пока нет заявок."}
          </p>
        ) : null}

        {!!filteredRequests.length ? (
          <div className={viewMode === "table" ? "requests-table-wrap" : "requests-list"}>
            {viewMode === "table" ? (
              <div className="requests-table-scroll">
                <table className="requests-table">
                  <thead>
                    <tr>
                      <th>Lemma</th>
                      <th>Рейтинг</th>
                      <th>Статус</th>
                      <th>Пользователь</th>
                      <th>Заметки</th>
                      <th>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRequests.map((request) => (
                      <tr key={request.documentId || request.id}>
                        <td className="requests-table-lemma">{request.lemma || "-"}</td>
                        <td className="requests-table-rating">{formatRating(request.rating)}</td>
                        <td>
                          {renderStatusBadge(request)}
                        </td>
                        <td>{request.user?.username || request.user?.email || "-"}</td>
                        <td className="requests-table-notes">{request.notes || "-"}</td>
                        <td>
                          <div className="requests-table-actions">{renderActionButtons(request)}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              paginatedRequests.map((request) => (
                <article className="requests-item" key={request.documentId || request.id}>
                  <div className="requests-item-top">
                    <div className="requests-item-lemma">
                      <i className="fa-solid fa-hand" aria-hidden="true"></i>
                      <span>{request.lemma || "-"}</span>
                    </div>
                    <div className="requests-item-actions">{renderRequestActions(request)}</div>
                  </div>
                  <div className="requests-item-row">
                    <strong><i className="fa-solid fa-star" aria-hidden="true"></i></strong>
                    <span>{formatRating(request.rating)}</span>
                  </div>
                  <div className="requests-item-row">
                    <strong><i className="fa-regular fa-user" aria-hidden="true"></i></strong>
                    <span>{request.user?.username || request.user?.email || "-"}</span>
                  </div>
                  <div className="requests-item-row">
                    <strong><i className="fa-regular fa-message" aria-hidden="true"></i></strong>
                    <span>{request.notes || "-"}</span>
                  </div>
                </article>
              ))
            )}

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        ) : null}
      </div>

      <RequestMergeModal
        isOpen={Boolean(mergeRequest)}
        request={mergeRequest}
        signRequests={signRequests}
        onClose={() => setMergeRequest(null)}
        onSuccess={({ updatedTranslations }) => {
          setMergeSuccessMessage(
            updatedTranslations
              ? `Merge выполнен. Обновлено переводов: ${updatedTranslations}.`
              : "Merge выполнен. Затронутых переводов не найдено.",
          );
        }}
      />

      <RequestPlaceholderUsageModal
        isOpen={Boolean(usageRequest)}
        request={usageRequest}
        onClose={() => setUsageRequest(null)}
      />
    </main>
  );
};

export default RequestsPage;
